import React, { useState, useEffect, useContext, useRef } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

// My Files
import BackButton from "../../BackButton";
import AvatarGroupDialog from "./AvatarGroupDialog";
import AvatarUploadButton from "../AvatarUploadButton";
import ToolbarWithLogo from "../../ToolbarWithLogo";
import { makeid } from "../../../utility";
import firebase from "../../../firebase";
import { DatabaseContext, UserContext } from "../../../context";
import EditGroupDialog from "./EditGroupDialog";
import DialogWithTextInput from "../../DialogWithTextInput";
import ConfirmDeleteDialog from "../../ConfirmDeleteDialog";
import NoPermissionsMessage from "../../NoPermissionsMessage";
import {
    errorTint,
    primaryTint,
    warningBorder,
    warningSurface,
} from "../../../surfaceTints";
import HubFileUsageDialog from "./usage/HubFileUsageDialog";
import HubUserUsageDialog from "./usage/HubUserUsageDialog";
import {
    buildHubDraftUpdates,
    cloneHubCalendar,
    cloneHubGroup,
} from "./hubDraftPersistence";

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

function ManagementCollection({
    actionLabel,
    children,
    description,
    empty,
    emptyText,
    icon,
    onAction,
    title,
}) {
    return (
        <Box
            sx={{
                minHeight: 210,
                p: 2,
                borderRadius: 2.5,
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.subtle",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    color: "primary.main",
                }}
            >
                {icon}
                <Box>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: "text.primary", fontWeight: 800 }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    alignContent: "flex-start",
                    gap: 1,
                }}
            >
                {empty ? (
                    <Typography variant="body2" color="text.secondary">
                        {emptyText}
                    </Typography>
                ) : (
                    children
                )}
            </Box>
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{ mt: 1.5 }}
            >
                {actionLabel}
            </Button>
        </Box>
    );
}

export default function NewHubPage() {
    const location = useLocation();
    const history = useHistory();

    const [hub, setHub] = useState({}); // Use this to pass the avatarEditor between components, and also to null the avatar when it's been changed

    const { users, folders, foldersLoaded, calendars, groups, events, files } =
        useContext(DatabaseContext);

    const [selectedGroup, setSelectedGroup] = useState({
        key: "",
        name: "",
        hub: "",
        users: [],
    });

    const [selectedCalendar, setSelectedCalendar] = useState({
        key: "",
        name: "",
    });

    const [hubGroups, setHubGroups] = useState([]);

    const [hubCalendars, setHubCalendars] = useState([]);

    // const [deletedCalendars, setDeletedCalendars] = useState([]);

    const [key, setKey] = useState("-" + makeid(19));
    const [name, setName] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedManagers, setSelectedManagers] = useState([]);

    const [imageURL, setImageURL] = useState("");
    const [otherManagers, setOtherManagers] = useState([]); // Platform users that are not selected as managers for this hub
    const [otherUsers, setOtherUsers] = useState([]); // Platform sers that are not selected as users for this hub

    const [hasChangedAvatar, setHasChangedAvatar] = useState(false);

    const [isInProgress, setIsInProgress] = useState(false);
    const [statusType, setStatusType] = useState("error");
    const [statusMessage, setStatusMessage] = useState("");
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveConfirmationIsVisible, setSaveConfirmationIsVisible] =
        useState(false);
    const hasUnsavedChangesRef = useRef(false);
    const allowNavigationRef = useRef(false);

    const [editGroupDialogIsOpen, setEditGroupDialogIsOpen] = useState(false);
    const [newGroupDialogIsOpen, setNewGroupDialogIsOpen] = useState(false);
    const [userUsageDialogIsOpen, setUserUsageDialogIsOpen] = useState(false);
    const [fileUsageDialogIsOpen, setFileUsageDialogIsOpen] = useState(false);

    // Owned by AvatarGroupDialog, mirrored here so the page knows they are up.
    const [managerEnrolementDialogIsOpen, setManagerEnrolementDialogIsOpen] =
        useState(false);
    const [userEnrolementDialogIsOpen, setUserEnrolementDialogIsOpen] =
        useState(false);

    const [newCalendarDialogIsOpen, setNewCalendarDialogIsOpen] =
        useState(false);

    const [editCalendarDialogIsOpen, setEditCalendarDialogIsOpen] =
        useState(false);

    const [
        confirmDeleteCalendarDialogOpen,
        setConfirmDeleteCalendarDialogOpen,
    ] = useState(false);

    const [calendarToDelete, setCalendarToDelete] = useState();

    const { currentUserData } = useContext(UserContext);

    const [userCanView, setUserCanView] = useState();

    const [confirmDeleteHubDialogIsOpen, setConfirmDeleteHubDialogIsOpen] =
        useState(false);

    const dialogIsOpen =
        editGroupDialogIsOpen ||
        newGroupDialogIsOpen ||
        newCalendarDialogIsOpen ||
        editCalendarDialogIsOpen ||
        confirmDeleteCalendarDialogOpen ||
        confirmDeleteHubDialogIsOpen ||
        managerEnrolementDialogIsOpen ||
        userEnrolementDialogIsOpen ||
        userUsageDialogIsOpen ||
        fileUsageDialogIsOpen ||
        saveConfirmationIsVisible;

    const unsavedChangesMessage =
        "You have unsaved changes. Leave this page without saving them?";

    function markUnsavedChanges() {
        allowNavigationRef.current = false;
        setSaveConfirmationIsVisible(false);

        if (!hasUnsavedChangesRef.current) {
            hasUnsavedChangesRef.current = true;
            setHasUnsavedChanges(true);
        }
    }

    function finishSaving() {
        allowNavigationRef.current = true;
        hasUnsavedChangesRef.current = false;
        setHasUnsavedChanges(false);
    }

    function discardChanges() {
        allowNavigationRef.current = true;
        hasUnsavedChangesRef.current = false;
        window.location.reload();
    }

    function goBackToHubs() {
        setSaveConfirmationIsVisible(false);
        history.push("/hubs");
    }

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (hasUnsavedChangesRef.current && !allowNavigationRef.current) {
                event.preventDefault();
                event.returnValue = "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            // detaching the listener
            if (!user) {
                // If no user is logged in, redirect to sign in page
                // history.replace("/signin");
                history.replace("/signin?url=" + location.pathname);
            }
        });

        if (currentUserData) {
            const urlParams = new URLSearchParams(location.search);
            const hubKey = urlParams.get("key");

            setKey(hubKey);

            let matchingHub = folders.find((f) => f.key === hubKey);
            let canView = false;

            if (
                matchingHub &&
                matchingHub.managers &&
                matchingHub.managers.includes(currentUserData.key)
            ) {
                canView = true;
            } else if (currentUserData.role === "System Digital Manager") {
                canView = true;
            }

            // console.log(matchingHub.key);

            if (matchingHub && canView) {
                // Editing a hub
                setUserCanView(true);

                if (hasUnsavedChangesRef.current) {
                    return () => unsubscribe();
                }

                setHub({
                    ...matchingHub,
                    ancestors: [...(matchingHub.ancestors || [])],
                    managers: [...(matchingHub.managers || [])],
                    users: [...(matchingHub.users || [])],
                });

                let selectedUsersArray = [];
                let otherUsersArray = [];

                let selectedManagersArray = [];
                let otherManagersArray = [];

                // Whether the person editing this hub has admin rights over a user
                const hasAdminRights = (user) =>
                    user.admins.includes(currentUserData.key) ||
                    user.createdBy === currentUserData.key ||
                    currentUserData.role === "System Digital Manager";

                users.forEach((savedUser) => {
                    const user = {
                        ...savedUser,
                        cannotRemoveAsManager: undefined,
                        cannotRemoveAsUser: undefined,
                    };

                    // If the user is a manager of this hub
                    if (
                        matchingHub.managers &&
                        matchingHub.managers.includes(user.key)
                    ) {
                        selectedManagersArray.push(user);

                        // If the manager is also in a hub higher than this one in the hierarchy, they can't be removed here.
                        matchingHub.ancestors.forEach((ancestorKey) => {
                            let ancestorFolder = folders.find(
                                (f) => f.key === ancestorKey,
                            );
                            if (
                                ancestorFolder &&
                                ancestorFolder.managers.filter(
                                    (u) => u === user.key,
                                ).length > 0
                            ) {
                                user.cannotRemoveAsManager = true;
                            }
                        });
                    } else {
                        if (
                            user.role !== "Player" &&
                            user.role !== "FAW Player" &&
                            user.role !== "Club Player"
                        ) {
                            otherManagersArray.push(user);
                        }
                    }

                    // If the user is a user of this hub. Every member of the hub is
                    // shown to its managers, whether or not they administer them, so
                    // that saving writes back the whole membership rather than just
                    // the part of it this manager can see.
                    if (
                        matchingHub.users &&
                        matchingHub.users.includes(user.key)
                    ) {
                        selectedUsersArray.push(user);

                        // If the user is also in a hub higher than this one in the hierarchy, they can't be removed here.
                        matchingHub.ancestors.forEach((ancestorKey) => {
                            let ancestorFolder = folders.find(
                                (f) => f.key === ancestorKey,
                            );
                            if (
                                ancestorFolder &&
                                ancestorFolder.users.filter(
                                    (u) => u === user.key,
                                ).length > 0
                            ) {
                                user.cannotRemoveAsUser = true;
                            }
                        });
                    } else if (hasAdminRights(user)) {
                        // Only users this manager administers can be added to the hub.
                        otherUsersArray.push(user);
                    }
                });

                setHubGroups(
                    groups
                        .filter((g) => g.hub === matchingHub.key)
                        .map(cloneHubGroup),
                );

                setName(matchingHub.name);
                setImageURL(matchingHub.imageURL);
                setHubCalendars(
                    calendars
                        .filter((c) => c.hub === matchingHub.key)
                        .map(cloneHubCalendar),
                );
                setSelectedManagers(selectedManagersArray);
                setSelectedUsers(selectedUsersArray);
                setOtherManagers(otherManagersArray);
                setOtherUsers(otherUsersArray);
            } else if (foldersLoaded) {
                setUserCanView(false);
            }
        }

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        history,
        currentUserData,
        key,
        folders,
        users,
        groups,
        location.search,
        calendars,
        foldersLoaded,
    ]);

    const managerSelectionDidChange = (selected, other) => {
        const selectedUserKeys = new Set(
            selectedUsers.map((selectedUser) => selectedUser.key),
        );
        const newManagers = selected.filter(
            (manager) =>
                !selectedManagers.some(
                    (selectedManager) => selectedManager.key === manager.key,
                ),
        );
        const managersToAddAsUsers = newManagers.filter(
            (manager) => !selectedUserKeys.has(manager.key),
        );

        if (managersToAddAsUsers.length > 0) {
            const addedManagerKeys = new Set(
                managersToAddAsUsers.map((manager) => manager.key),
            );
            setSelectedUsers((currentUsers) => [
                ...currentUsers,
                ...managersToAddAsUsers,
            ]);
            setOtherUsers((currentUsers) =>
                currentUsers.filter((user) => !addedManagerKeys.has(user.key)),
            );
            setHubGroups((currentGroups) =>
                currentGroups.map((group) =>
                    group.name === "All Users"
                        ? {
                              ...group,
                              users: [
                                  ...(group.users || []),
                                  ...managersToAddAsUsers,
                              ],
                          }
                        : group,
                ),
            );
        }

        setSelectedManagers([...selected]);
        setOtherManagers([...other]);
        markUnsavedChanges();
    };

    const userSelectionDidChange = (selected, other) => {
        const selectedUserKeys = new Set(selected.map((user) => user.key));
        const missingManagers = selectedManagers.filter(
            (manager) => !selectedUserKeys.has(manager.key),
        );
        const nextSelectedUsers = [...selected, ...missingManagers];
        const nextSelectedUserKeys = new Set(
            nextSelectedUsers.map((user) => user.key),
        );
        const nextOtherUsers = other.filter(
            (user) => !nextSelectedUserKeys.has(user.key),
        );

        setSelectedUsers(nextSelectedUsers);
        setOtherUsers(nextOtherUsers);
        setHubGroups((currentGroups) =>
            currentGroups.map((group) => ({
                ...group,
                users:
                    group.name === "All Users"
                        ? nextSelectedUsers
                        : (group.users || []).filter((user) =>
                              nextSelectedUserKeys.has(user.key),
                          ),
            })),
        );
        markUnsavedChanges();
    };

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setStatusIsVisible(false);
    };

    function handleSaveClick() {
        editHub();
    }

    function handleChange(event) {
        let newValue = event.target.value;

        if (event.target.id === "name") {
            setName(newValue);
            markUnsavedChanges();
        }
    }

    async function editHub() {
        setIsInProgress(true);

        try {
            const nextImageURL = hasChangedAvatar
                ? await uploadAvatar()
                : imageURL;
            const updates = buildHubDraftUpdates({
                hub,
                name,
                imageURL: nextImageURL,
                selectedUsers,
                selectedManagers,
                hubGroups,
                hubCalendars,
                folders,
                files,
                groups,
                calendars,
                events,
                users,
            });

            await firebase.database().ref().update(updates);

            finishSaving();
            setHub((savedHub) => ({
                ...savedHub,
                name,
                imageURL: nextImageURL,
                managers: selectedManagers.map((manager) => manager.key),
                users: selectedUsers.map((user) => user.key),
                avatar: null,
                avatarEditor: undefined,
            }));
            setImageURL(nextImageURL);
            setHasChangedAvatar(false);
            setHubGroups((draftGroups) =>
                draftGroups
                    .filter((group) => !group.toDelete)
                    .map((group) => ({ ...group, toDelete: undefined })),
            );
            setHubCalendars((draftCalendars) =>
                draftCalendars
                    .filter((calendar) => !calendar.toDelete)
                    .map((calendar) => ({
                        ...calendar,
                        toDelete: undefined,
                    })),
            );
            setSaveConfirmationIsVisible(true);
        } catch (error) {
            const errorMessage = error?.message || "Unable to save hub";
            console.error("Unable to save hub", error);
            setStatusMessage(errorMessage);
            setStatusType("error");
            setStatusIsVisible(true);
        } finally {
            setIsInProgress(false);
        }
    }

    function uploadAvatar() {
        return new Promise((resolve, reject) => {
            if (hub.avatarEditor) {
                var storage = firebase.storage();

                hub.avatarEditor
                    .getImageScaledToCanvas()
                    .toBlob(function (blob) {
                        // Upload the new file
                        let avatarRef = storage
                            .ref()
                            .child("avatars/hubs/" + key + "/avatar.png");

                        avatarRef
                            .put(blob)
                            .then((snapshot) => {
                                // Upload was successful
                                console.log("Upload Successful");
                                hub.avatar = null;

                                avatarRef.getDownloadURL().then((url) => {
                                    resolve(url);
                                });
                            })
                            .catch((error) => {
                                let errorCode = error.code;
                                let errorMessage = error.message;
                                console.log(errorCode + ":" + errorMessage);
                                reject(error);
                            });
                    });
            } else {
                resolve("none");
            }
        });
    }

    function didChangeAvatar() {
        setHasChangedAvatar(true);
        markUnsavedChanges();
    }

    function handleCloseEditGroupDialog(newGroupUsers, groupToEdit) {
        if (newGroupUsers && groupToEdit) {
            setHubGroups((currentGroups) =>
                currentGroups.map((group) =>
                    group.key === groupToEdit.key
                        ? {
                              ...group,
                              name: groupToEdit.name,
                              users: [...newGroupUsers],
                          }
                        : group,
                ),
            );
            markUnsavedChanges();
        }

        setEditGroupDialogIsOpen(false);
        setSelectedGroup({
            key: "",
            name: "",
            hub: "",
            users: [],
        });
    }

    function handleUserUsageClick() {
        setUserUsageDialogIsOpen(true);
    }

    function handleFileUsageClick() {
        setFileUsageDialogIsOpen(true);
    }

    const handleEditGroupClick = (group) => () => {
        setSelectedGroup(group);
        setEditGroupDialogIsOpen(true);
    };

    function handleAddGroupButtonPressed() {
        setNewGroupDialogIsOpen(true);
    }

    function handleCloseNewGroupDialog(groupName, okPressed) {
        if (groupName !== "" && groupName !== "All Users" && okPressed) {
            setHubGroups((currentGroups) => [
                ...currentGroups,
                {
                    key: "-" + makeid(19),
                    name: groupName,
                    hub: key,
                    users: [],
                },
            ]);
            markUnsavedChanges();
        }

        setNewGroupDialogIsOpen(false);
    }

    function handleCloseGroupDialogAfterDelete(groupToDelete) {
        const groupHasBeenSaved = groups.some(
            (group) => group.key === groupToDelete.key,
        );
        setHubGroups((currentGroups) =>
            groupHasBeenSaved
                ? currentGroups.map((group) =>
                      group.key === groupToDelete.key
                          ? { ...group, toDelete: true }
                          : group,
                  )
                : currentGroups.filter(
                      (group) => group.key !== groupToDelete.key,
                  ),
        );
        markUnsavedChanges();

        setSelectedGroup({
            key: "",
            name: "",
            hub: "",
            users: [],
        });

        setEditGroupDialogIsOpen(false);
    }

    function handleCloseNewCalendarDialog(calendarName, okPressed) {
        if (calendarName !== "" && okPressed) {
            setHubCalendars((currentCalendars) => [
                ...currentCalendars,
                { key: makeid(19), name: calendarName, hub: key },
            ]);
            markUnsavedChanges();
        }

        setNewCalendarDialogIsOpen(false);
    }

    function handleCloseEditCalendarDialog(calendarName, okPressed) {
        if (calendarName !== "" && okPressed) {
            setHubCalendars((currentCalendars) =>
                currentCalendars.map((calendar) =>
                    calendar.key === selectedCalendar.key
                        ? { ...calendar, name: calendarName }
                        : calendar,
                ),
            );
            markUnsavedChanges();
        }

        setSelectedCalendar({ key: "", name: "" });
        setEditCalendarDialogIsOpen(false);
    }

    const handleCalendarPressed = (calendar) => () => {
        setSelectedCalendar(calendar);
        setEditCalendarDialogIsOpen(true);
    };

    function handleAddCalendarButtonPressed() {
        setNewCalendarDialogIsOpen(true);
    }

    const handleCalendarChipDeletePressed = (calendar) => () => {
        setCalendarToDelete(calendar);
        setConfirmDeleteCalendarDialogOpen(true);
    };

    function handleDeleteButtonPressed() {
        setConfirmDeleteHubDialogIsOpen(true);
    }

    function handleCloseOnDeleteHubDialog(result) {
        if (result === true && currentUserData) {
            if (
                hub.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager"
            ) {
                let childFolders = folders.filter((f) =>
                    f.ancestors.includes(hub.key),
                ).length;

                let childFiles = files.filter((f) =>
                    f.ancestors.includes(hub.key),
                ).length;

                if (childFolders + childFiles > 0) {
                    setStatusType("error");
                    setStatusMessage(
                        "This hub contains files and/or folders, you need to delete them before deleting the hub",
                    );
                    setStatusIsVisible(true);
                } else {
                    deleteHub();
                }
            }
        }

        setConfirmDeleteHubDialogIsOpen(false);
    }

    function handleCloseUserUsageDialog() {
        setUserUsageDialogIsOpen(false);
    }

    function handleCloseFileUsageDialog() {
        setFileUsageDialogIsOpen(false);
    }

    function deleteHub() {
        console.log("DELETE: " + hub.key);

        // Delete Avatar from storage
        var storage = firebase.storage();
        let avatarRef = storage
            .ref()
            .child("avatars/hubs/" + key + "/avatar.png");

        avatarRef.delete();

        // Delete Events

        // Delete Calendars and events from database
        calendars.forEach((calendar) => {
            if (calendar.hub === hub.key) {
                // Delete the events for this calendar
                calendar.events.forEach((event) => {
                    let eventRef = firebase
                        .database()
                        .ref("events")
                        .child(event.key);

                    eventRef.remove();
                });

                // Delete the calendar
                let calendarRef = firebase
                    .database()
                    .ref("calendars")
                    .child(calendar.key);
                calendarRef.remove();
            }
        });

        // Delete groups from database
        groups.forEach((group) => {
            if (group.hub === hub.key) {
                let groupRef = firebase
                    .database()
                    .ref("groups")
                    .child(group.key);
                groupRef.remove();
            }
        });

        // Delete hub from database
        let hubRef = firebase.database().ref("folders").child(hub.key);
        hubRef.remove();

        finishSaving();
        history.goBack();
    }

    function handleCloseOnDeleteCalendarDialog(result) {
        if (result === true && currentUserData && calendarToDelete) {
            const calendarHasBeenSaved = calendars.some(
                (calendar) => calendar.key === calendarToDelete.key,
            );
            setHubCalendars((currentCalendars) =>
                calendarHasBeenSaved
                    ? currentCalendars.map((calendar) =>
                          calendar.key === calendarToDelete.key
                              ? { ...calendar, toDelete: true }
                              : calendar,
                      )
                    : currentCalendars.filter(
                          (calendar) => calendar.key !== calendarToDelete.key,
                      ),
            );
            markUnsavedChanges();
        }

        setCalendarToDelete(null);
        setConfirmDeleteCalendarDialogOpen(false);
    }

    const optionalGroups = hubGroups.filter(
        (group) => !group.toDelete && group.name !== "All Users",
    );
    const activeHubCalendars = hubCalendars.filter(
        (calendar) => !calendar.toDelete,
    );

    return (
        <>
            <Prompt
                when={hasUnsavedChanges}
                message={() =>
                    allowNavigationRef.current ? true : unsavedChangesMessage
                }
            />
            {userCanView && (
                <>
                    <Box
                        component="main"
                        sx={{
                            minHeight: "calc(100vh - 64px)",
                            backgroundColor: "background.default",
                            pt: { xs: 2, sm: 3, md: 4 },
                            pb: { xs: 2, sm: 4 },
                        }}
                    >
                        <Container
                            maxWidth="md"
                            sx={{ px: { xs: 1.5, sm: 3 } }}
                        >
                            <Card
                                variant="outlined"
                                sx={{
                                    overflow: "hidden",
                                    borderRadius: 3,
                                    borderColor: "divider",
                                    boxShadow: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "0 8px 28px rgba(0, 0, 0, 0.3)"
                                            : "0 8px 28px rgba(20, 35, 25, 0.075)",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.25,
                                        p: { xs: 2, sm: 3 },
                                    }}
                                >
                                    <BackButton />
                                    <Box
                                        sx={{
                                            width: 46,
                                            height: 46,
                                            borderRadius: 2.5,
                                            display: {
                                                xs: "none",
                                                sm: "grid",
                                            },
                                            placeItems: "center",
                                            color: "primary.main",
                                            backgroundColor: primaryTint,
                                        }}
                                    >
                                        <AccountTreeOutlinedIcon />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="h4"
                                            component="h1"
                                            sx={{
                                                fontSize: { xs: 26, sm: 31 },
                                                lineHeight: 1.15,
                                            }}
                                        >
                                            Manage hub
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.4 }}
                                        >
                                            Update this hub’s profile, access
                                            and organisation.
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Delete hub">
                                        <IconButton
                                            color="error"
                                            aria-label="Delete hub"
                                            onClick={handleDeleteButtonPressed}
                                            sx={{
                                                backgroundColor: errorTint,
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Divider />

                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, sm: 4 },
                                        "&:last-child": {
                                            pb: { xs: 2.5, sm: 4 },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            mb: 2.5,
                                        }}
                                    >
                                        <AvatarUploadButton
                                            recipient={hub}
                                            didChangeAvatar={didChangeAvatar}
                                            path="hubs"
                                            centered
                                            compact
                                        />
                                    </Box>
                                    <TextField
                                        id="name"
                                        label="Hub name"
                                        fullWidth
                                        value={name}
                                        onChange={handleChange}
                                    />

                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" component="h2">
                                        Access
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.35, mb: 2 }}
                                    >
                                        Choose who manages and belongs to this
                                        hub.
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                            },
                                            gap: 2,
                                        }}
                                    >
                                        <AvatarGroupDialog
                                            title="Managers"
                                            selectedUsers={selectedManagers}
                                            otherUsers={otherManagers}
                                            onChange={managerSelectionDidChange}
                                            onOpenChange={
                                                setManagerEnrolementDialogIsOpen
                                            }
                                        />
                                        <AvatarGroupDialog
                                            title="Users"
                                            selectedUsers={selectedUsers}
                                            otherUsers={otherUsers}
                                            onChange={userSelectionDidChange}
                                            onOpenChange={
                                                setUserEnrolementDialogIsOpen
                                            }
                                        />
                                    </Box>

                                    <Divider sx={{ my: 3 }} />
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                            },
                                            gap: 2,
                                        }}
                                    >
                                        <ManagementCollection
                                            icon={<FolderOutlinedIcon />}
                                            title="Groups"
                                            description="Organise hub users."
                                            empty={optionalGroups.length === 0}
                                            emptyText="No optional groups yet."
                                            actionLabel="New group"
                                            onAction={
                                                handleAddGroupButtonPressed
                                            }
                                        >
                                            {optionalGroups.map((group) => (
                                                <Chip
                                                    key={group.key}
                                                    label={`${group.name} (${group.users.length})`}
                                                    onClick={handleEditGroupClick(
                                                        group,
                                                    )}
                                                    variant="outlined"
                                                />
                                            ))}
                                        </ManagementCollection>
                                        <ManagementCollection
                                            icon={<CalendarMonthOutlinedIcon />}
                                            title="Calendars"
                                            description="Manage hub events."
                                            empty={
                                                activeHubCalendars.length === 0
                                            }
                                            emptyText="No calendars yet."
                                            actionLabel="New calendar"
                                            onAction={
                                                handleAddCalendarButtonPressed
                                            }
                                        >
                                            {activeHubCalendars.map(
                                                (calendar) => (
                                                    <Chip
                                                        key={calendar.key}
                                                        label={calendar.name}
                                                        onDelete={handleCalendarChipDeletePressed(
                                                            calendar,
                                                        )}
                                                        onClick={handleCalendarPressed(
                                                            calendar,
                                                        )}
                                                        variant="outlined"
                                                    />
                                                ),
                                            )}
                                        </ManagementCollection>
                                    </Box>

                                    <Divider sx={{ my: 3 }} />
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <DataUsageOutlinedIcon
                                            sx={{ color: "primary.main" }}
                                        />
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                component="h2"
                                            >
                                                Usage data
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Review activity within this hub.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                            },
                                            gap: 1.5,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            startIcon={<PersonOutlineIcon />}
                                            onClick={handleUserUsageClick}
                                            sx={{ py: 1.25 }}
                                        >
                                            User usage
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<FolderOutlinedIcon />}
                                            onClick={handleFileUsageClick}
                                            sx={{ py: 1.25 }}
                                        >
                                            File usage
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Container>
                    </Box>

                    {hasUnsavedChanges && !dialogIsOpen && (
                        <Box
                            role="status"
                            aria-live="polite"
                            sx={{
                                position: "fixed",
                                left: { xs: 12, sm: "auto" },
                                right: { xs: 12, sm: 24 },
                                bottom: { xs: 16, sm: 80 },
                                zIndex: (theme) => theme.zIndex.snackbar - 1,
                                display: "flex",
                                alignItems: { xs: "stretch", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1.5,
                                maxWidth: 720,
                                p: 1.5,
                                borderRadius: 2.5,
                                color: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "warning.light"
                                        : "warning.dark",
                                border: "1px solid",
                                borderColor: warningBorder,
                                backgroundColor: warningSurface,
                                boxShadow: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "0 14px 36px rgba(0, 0, 0, 0.45)"
                                        : "0 14px 36px rgba(20, 35, 25, 0.2)",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flex: 1,
                                }}
                            >
                                <WarningAmberOutlinedIcon
                                    sx={{ flexShrink: 0 }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 800,
                                        whiteSpace: {
                                            xs: "normal",
                                            sm: "nowrap",
                                        },
                                    }}
                                >
                                    Changes haven&apos;t been saved yet
                                </Typography>
                            </Box>
                            <Button
                                variant="text"
                                color="inherit"
                                disabled={isInProgress}
                                onClick={discardChanges}
                                sx={{ flexShrink: 0 }}
                            >
                                Discard changes
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={isInProgress}
                                onClick={handleSaveClick}
                                startIcon={
                                    isInProgress ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <SaveOutlinedIcon />
                                    )
                                }
                                sx={{ flexShrink: 0 }}
                            >
                                {isInProgress ? "Saving" : "Save changes"}
                            </Button>
                        </Box>
                    )}

                    <Dialog
                        open={saveConfirmationIsVisible}
                        onClose={() => setSaveConfirmationIsVisible(false)}
                        aria-labelledby="hub-save-confirmation-dialog-title"
                        fullWidth
                        maxWidth="xs"
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: 3,
                                    overflow: "hidden",
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                px: { xs: 2, sm: 3 },
                                py: 2.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 2,
                                    display: "grid",
                                    placeItems: "center",
                                    flexShrink: 0,
                                    color: "primary.main",
                                    backgroundColor: "action.hover",
                                }}
                            >
                                <CheckCircleOutlineIcon />
                            </Box>
                            <Typography
                                id="hub-save-confirmation-dialog-title"
                                variant="h5"
                                component="h2"
                                sx={{ flex: 1, minWidth: 0, lineHeight: 1.1 }}
                            >
                                Changes saved
                            </Typography>
                            <IconButton
                                aria-label="Dismiss save confirmation"
                                onClick={() =>
                                    setSaveConfirmationIsVisible(false)
                                }
                                sx={{ mt: -0.75, mr: -1 }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <DialogContent
                            dividers
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                backgroundColor: "background.default",
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.6 }}
                                >
                                    The hub&apos;s details, access and
                                    organisation settings have been saved
                                    successfully.
                                </Typography>
                            </Box>
                        </DialogContent>

                        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                            <Button
                                color="inherit"
                                onClick={() =>
                                    setSaveConfirmationIsVisible(false)
                                }
                            >
                                Dismiss
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={goBackToHubs}
                            >
                                Back to hubs
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <EditGroupDialog
                        open={editGroupDialogIsOpen}
                        onClose={handleCloseEditGroupDialog}
                        onDelete={handleCloseGroupDialogAfterDelete}
                        hubUsers={selectedUsers}
                        group={selectedGroup}
                        hub={hub}
                    />
                    <DialogWithTextInput
                        open={newGroupDialogIsOpen}
                        onClose={handleCloseNewGroupDialog}
                        title="New group"
                        message="Enter a name for the new group."
                        modern
                        confirmLabel="Add to draft"
                    />
                    <DialogWithTextInput
                        open={newCalendarDialogIsOpen}
                        onClose={handleCloseNewCalendarDialog}
                        title="New calendar"
                        message="Enter a name for the new calendar."
                        modern
                        confirmLabel="Add to draft"
                    />
                    <DialogWithTextInput
                        open={editCalendarDialogIsOpen}
                        onClose={handleCloseEditCalendarDialog}
                        title="Edit calendar"
                        message="Enter a name for the calendar."
                        initialValue={selectedCalendar.name}
                        modern
                        confirmLabel="Apply to draft"
                    />
                    {confirmDeleteCalendarDialogOpen && (
                        <ConfirmDeleteDialog
                            title="Delete Calendar"
                            message="Are you sure you want to delete this calendar and all its events?"
                            supportingMessage="The calendar and its events will not be deleted until you save the hub."
                            open={confirmDeleteCalendarDialogOpen}
                            onClose={handleCloseOnDeleteCalendarDialog}
                        />
                    )}
                    {confirmDeleteHubDialogIsOpen && (
                        <ConfirmDeleteDialog
                            title="Delete Hub"
                            message="Are you sure you want to delete this hub?"
                            open={confirmDeleteHubDialogIsOpen}
                            onClose={handleCloseOnDeleteHubDialog}
                        />
                    )}
                    {userUsageDialogIsOpen && (
                        <HubUserUsageDialog
                            open={userUsageDialogIsOpen}
                            onClose={handleCloseUserUsageDialog}
                            hub={hub}
                        />
                    )}
                    {fileUsageDialogIsOpen && (
                        <HubFileUsageDialog
                            open={fileUsageDialogIsOpen}
                            onClose={handleCloseFileUsageDialog}
                            hub={hub}
                        />
                    )}
                </>
            )}
            {userCanView === false && <NoPermissionsMessage />}
            {userCanView === undefined && (
                <Box
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <CircularProgress size={32} aria-label="Loading hub" />
                </Box>
            )}
            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={handleStatusClose}
            >
                <Alert onClose={handleStatusClose} severity={statusType}>
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </>
    );
}
